import { Component, ElementRef, OnChanges, SimpleChanges, output, viewChild, input, model, inject } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { noop } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';
import { ApInputConstraintService } from '@ap/input/constraint';
import { HelpText, HelpTextPosition } from '@ap/form';

import { InputType } from './input.type';
import { InputmodeType } from './inputmode.type';
import { ApOption, ApOptions } from './autocomplete/option/option.model';
import { ApAutocompleteFilterService } from './autocomplete';

@Component({
    template: ''
})
export abstract class AbstractInputComponent implements ControlValueAccessor, OnChanges, HelpText {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);
    protected readonly filterService? = inject(ApAutocompleteFilterService);

    /**
     * Emits event if icon is clicked. Only applicable for elements with icon
     * @internal
     */
    public readonly iconClick = output<string>();

    /**
     * Type of input (Standard HTML types)
     * @internal
     */
    readonly input = viewChild<ElementRef>('input');

    /**
     * Field label. Shown in the field. Moves up if focussed, filled in or a prefix is set
     */
    public readonly label = model<string>(undefined);

    /**
     * Description of field. Shown above the field
     */
    public readonly description = input<string>(undefined);

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>(undefined);

    /**
     * Help text shown under field. Disappears if errors are present
     * Some inputs have a default value. If `undefined` is passed or nothing is passed
     * it will show the default. If you want to remove the helpText completely pass `null`
     */
    public readonly helpText = model<string>(undefined);

    /**
     * Help text position, either 'below' (default) or 'above' the input field.
     */
    public readonly helpTextPosition = input<HelpTextPosition>(HelpTextPosition.BELOW);

    /**
     * Expose `HelpTextPosition` enum for template
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected readonly HelpTextPosition = HelpTextPosition;

    /**
     * Emits event if input is blurred
     */
    public readonly inputBlur = output<void>();

    /**
     * Characters that are stripped
     */
    protected readonly stripPattern = input<RegExp>(undefined);

    /**
     * The value to suggest when using this.applySuggestion()
     */
    protected suggestionValue: string;

    /**
     * Maxlength of input
     */
    protected readonly maxlength = input<number>(null);

    /**
     * Action Icon
     */
    protected readonly icon = input(null);

    /**
     * Prefix in front of input
     */
    protected readonly prefix = input(null);

    /**
     * Type of input (Standard HTML types)
     */
    protected readonly type = input<InputType>('text');

    /**
     * Inputmode (Standard HTML inputmode types)
     */
    protected readonly inputmode = input<InputmodeType>(null);

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `input-${Math.round(Math.random() * 10000000)}`;

    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    /**
     * Type of input (Standard HTML types)
     * @internal
     */
    readonly container = viewChild<ElementRef>('container');

    /**
     * AutoComplete options. It can be a list of strings or objects.
     */
    public readonly options = input<ApOptions>(undefined);

    /**
     * The keys to search and show when autoComplete options is an object array. Can be a single string or an array of strings.
     */
    public readonly optionKey = input<string | string[]>(undefined);

    /**
     * Native html autocomplete value. For a complete list see: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete#values
     * Note: This is not the option list that appears beneath the input. To show this pass the `options` property.
     * This uses the [native browser autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/autocomplete) which autofills forms based on information from your browser.
     * When turning this on the custom autocomplete list below the input will not be shown (even if you pass options).
     */
    public readonly autocomplete = input('off', {
        transform: (value: string): string => {
            return !value || value.length === 0 ? 'off' : value;
        }
    });

    /**
     * Event emitter to distinguish between changes from autoComplete choices and 'manual' input
     */
    readonly selectionChange = output<ApOption>();

    constructor() {
        this.control && (this.control.valueAccessor = this);
    }

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    /** @internal */
    public isDisabled = false;

    /**
     * Key from help text to get
     */
    private helpTextKey: string;
    private _value = '';
    private _valueDisplay = '';

    public get value(): string {
        return this._value;
    }

    public set value(val: string) {
        this._value = val;
    }

    protected get valueDisplay(): string {
        return this._valueDisplay;
    }

    protected set valueDisplay(val: string) {
        this._valueDisplay = val;
    }

    /**
     * Angular lifecycle hook.
     * If `helpText` changes determine correct help-text to show.
     * When `options` or `optionKey` changer recalculate internal equivalents.
     */
    public ngOnChanges(changes?: SimpleChanges): void {
        if (changes?.helpText) {
            this.setHelpText();
        }
    }

    /**
     * Handle change of input
     */
    protected onInputChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        const allowedValue = this.inputConstraintService.strip(value, this.stripPattern());

        if (value !== allowedValue) {
            const { nativeElement } = this.input();
            event.stopPropagation();
            nativeElement.value = allowedValue;
            // prevent caret from jumping to end when infixing
            const caret = nativeElement.selectionStart - 1;
            nativeElement.setSelectionRange(caret, caret);
        }

        this.value = allowedValue;
        this.onChange(this.value);
    }

    /**
     * Format value on inputBlur
     */
    protected onBlur(): void {
        if (this.value) {
            const formattedValue = this.formatValue(this.value);
            const formattedValueReadable = this.formatValueDisplay(this.value);

            // If value has been formatted set it and notify change
            if (this.value !== formattedValue || this.valueDisplay !== formattedValueReadable) {
                this.value = formattedValue;
                this.valueDisplay = formattedValueReadable;
                this.onChange(this.value);
            }
        } else if (this.valueDisplay) {
            this.valueDisplay = this.value;
        }

        this.inputBlur.emit(undefined);
        this.onTouched();
    }

    /**
     * Placeholder function that inputs can overwrite if they
     * want to do something with the search trigger
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onSearch(event: Event): void {}

    /**
     * Placeholder function that inputs can overwrite if they
     * want to do something with the icon click
     */
    protected handleIconClick(event: Event): void {
        event.preventDefault();
        this.iconClick.emit(this.value);
        this.input().nativeElement.focus();
    }

    /**
     * Placeholder function that inputs can overwrite
     */
    protected formatValue(value: string): string {
        return value;
    }

    /**
     * Placeholder function that inputs can overwrite
     */
    protected formatValueDisplay(value: string): string {
        return this.formatValue(value);
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(value: any): void {
        this.value = value;
        this.valueDisplay = value ? this.formatValueDisplay(value) : value;
    }

    /**
     * Aria described by contains conditional fields that describe the input field
     * */
    protected get ariaDescribedBy(): string | null {
        const ids = [
            ...(this.description() ? [`${this.uniqueId}-description`] : []),
            ...(this.control ? [`${this.uniqueId}-errors`] : []),
            ...(this.helpText() ? [`${this.uniqueId}-help-text`] : [])
        ].join(' ');

        return ids.length > 0 ? ids : null;
    }

    /**
     * Set default content per component
     */
    protected setDefaultContent(content: any, contentPrefix: string): void {
        this.contentService.addContentItems(content);

        this.content = content;
        this.label.set(this.contentService.get(`${contentPrefix}.label`));
        this.helpTextKey = `${contentPrefix}.helptext`;
        this.setHelpText();
    }

    protected showSuggestion(suggestion: string): void {
        this.suggestionValue = suggestion;
    }

    protected applySuggestion(): void {
        const { suggestionValue } = this;
        if (suggestionValue) {
            this.suggestionValue = null;
            this.value = suggestionValue;
            this.valueDisplay = suggestionValue;
            this.onChange(suggestionValue);
        }
    }

    /**
     * Checks which help text to set:
     * - If set from outside use that help text
     * - If null, leave it null so nothing will be shown
     * - Otherwise, try to find the default value
     */
    private setHelpText(): void {
        // Help text is set from outside don't do anything
        const helpText = this.helpText();
        if (helpText || helpText === null) {
            return;
        }
        if (this.content?.[this.helpTextKey]) {
            this.helpText.set(this.contentService.get(this.helpTextKey));
        }
    }

    /**
     * Filter autocomplete options on search value
     */
    protected get filteredOptions(): ApOptions {
        return this.filterService?.filter(this.input().nativeElement?.value?.toLowerCase() || '', this.options(), this.optionKey()) || [];
    }

    /**
     * Get element for autocomplete to connect to
     */
    protected get connectedElement(): ElementRef {
        return this.container();
    }

    /**
     * Is this field required
     */
    protected get isRequired(): boolean {
        if (!this.control?.control) {
            return false;
        }
        const { control } = this.control;
        return control.hasValidator(Validators.required);
    }

    /**
     * Select autocomplete option from click
     */
    protected optionSelect(option: ApOption): void {
        this.setAutocompleteValue(option);
    }

    /**
     * Set the value when autocomplete choice is made
     */
    protected setAutocompleteValue(value: ApOption): void {
        const isString = typeof value === 'string';
        const stringValue = isString ? value : value[this.filterService.getLabelKey(this.optionKey())];

        this.writeValue(stringValue);

        this.onChange(stringValue);
        this.selectionChange.emit(this.filterService.optionsAreStrings(this.options()) ? stringValue : value);

        this.onTouched();
    }
}
